import { Controller } from "@hotwired/stimulus"

import { createFormHelpers } from "../../modules/admissoes/form_helpers"
import { initInitialState } from "../../modules/admissoes/initial_state"
import { initHorarioEscala } from "../../modules/admissoes/horario_escala"
import { initDependentesDocs } from "../../modules/admissoes/dependentes_docs"
import { initReservista } from "../../modules/admissoes/reservista"
import { initBeneficios } from "../../modules/admissoes/beneficios"
import { initCargoSalario } from "../../modules/admissoes/cargo_salario"
import { initDefaultsApi } from "../../modules/admissoes/defaults_api"
import { initAdiantamento } from "../../modules/admissoes/adiantamento"
import { initCnh } from "../../modules/admissoes/cnh"
import { initPensao } from "../../modules/admissoes/pensao"
import { initUppercase } from "../../modules/admissoes/uppercase"
import { initWizard } from "../../modules/admissoes/wizard"
import { initSectionProgress } from "../../modules/admissoes/section_progress"

// Controller: admissoes--form
export default class extends Controller {
  static values = {
    salariosUrl: String,
    defaultsUrl: String,
  }

  connect() {
    if (typeof window.$j === "undefined") return
    const $j = window.$j

    const $root = $j(this.element)
    // Busca o formulário pelo ID fixo; fallback para qualquer <form> dentro do controller.
    const $form = $root.find("#new_movimentacoes_pessoal, form").first()
    if (!$form.length) return

    if (this._initialized) return

    const helpers = createFormHelpers($j, $form)
    const api = {
      salariosUrl: this.salariosUrlValue,
      defaultsUrl: this.defaultsUrlValue,
    }

    // guarda módulos pra destruir no disconnect
    this._admissaoModules = []

    // 1) estado inicial (hide de campos + limpeza de textos)
    this._admissaoModules.push(initInitialState($form, helpers))

    // 2) features (cada uma liga seus events / apply / destroy)
    const horarioEscala = initHorarioEscala($form, helpers)
    const dependentes = initDependentesDocs($form, helpers)
    const reservista = initReservista($form, helpers)
    const beneficios = initBeneficios($form, helpers)
    const cargoSalario = initCargoSalario($j, $form, helpers, api)
    const adiantamento = initAdiantamento($j, $form, helpers)
    const cnh = initCnh($form, helpers)
    const pensao = initPensao($form, helpers)
    const uppercase = initUppercase($form)

    this._admissaoModules.push(
      horarioEscala,
      dependentes,
      reservista,
      beneficios,
      cargoSalario,
      adiantamento,
      cnh,
      pensao,
      uppercase
    )

    // 3) aplica estado coerente (1ª render)
    horarioEscala.apply()
    dependentes.apply()
    reservista.apply()
    beneficios.apply()
    if (adiantamento.apply) adiantamento.apply()
    if (cargoSalario.apply) cargoSalario.apply()
    cnh.apply()
    pensao.apply()

    // Marca como inicializado APÓS todos os módulos estarem prontos
    // (posição aqui garante que um erro nos módulos acima não "trave"
    //  o guard impedindo re-tentativa em reconexões Turbo)
    this._initialized = true

    // 5) wizard de etapas (inicializa no elemento raiz do controller)
    this._wizard = initWizard(this.element)

    // Bloqueia "Próximo" na etapa 1 enquanto CPF tiver restrição local
    const $nextBtn = $root.find('[data-wizard-nav="next"]')
    $root.on('cpf:bloqueado', () => $nextBtn.prop('disabled', true).attr('title', 'Resolva a pendência do CPF antes de continuar.'))
    $root.on('cpf:liberado',  () => $nextBtn.prop('disabled', false).removeAttr('title'))

    // Listener: quando crud--form exibe erros de validação, navega ao step
    // contendo o primeiro campo marcado como inválido (classe crud-invalid).
    this._onCrudErrors = () => this._navigateToFirstInvalidField()
    this.element.addEventListener('crud:errors-shown', this._onCrudErrors)

    // 6) indicador de completude por etapa
    this._sectionProgress = initSectionProgress(this.element)
    if (this._sectionProgress) this._admissaoModules.push(this._sectionProgress)

    // 4) defaults da API — inicializado APÓS wizard e sectionProgress para que o
    // afterApply possa atualizar o estado dos indicadores e a navegação do wizard
    // com o estado real dos campos (após a API remover obrigatoriedades, etc.).
    const defaults = initDefaultsApi($j, $form, helpers, api, {
      afterApply: () => {
        beneficios.apply()
        reservista.apply()
        dependentes.apply()
        horarioEscala.apply()
        if (adiantamento.apply) adiantamento.apply()
        if (cargoSalario.apply) cargoSalario.apply()
        // Restaura step salvo (?step=N) ou navega ao primeiro incompleto em novas admissões.
        // Em edição sem ?step= na URL, fica na etapa 0 — o usuário navega por conta própria.
        const stepParam = new URLSearchParams(window.location.search).get('step')
        const isEditingExisting = $form.find('input[name="_method"]').length > 0
        if (stepParam || !isEditingExisting) {
          this._wizard?.navigateToFirstIncomplete()
        }
        // Atualiza os indicadores de completude após a navegação
        this._sectionProgress?.refresh()
      },
    })
    this._admissaoModules.push(defaults)
  }

  disconnect() {
    this._initialized = false

    if (this._onCrudErrors) {
      this.element.removeEventListener('crud:errors-shown', this._onCrudErrors)
    }

    if (typeof window.$j === "undefined") return
    const $j = window.$j
    const $form = $j(this.element).find("#new_movimentacoes_pessoal").first()
    if (!$form.length) return

      ; (this._admissaoModules || []).forEach((m) => {
        try {
          if (m && typeof m.destroy === "function") m.destroy()
        } catch (_e) { }
      })

    this._admissaoModules = []
  }

  _navigateToFirstInvalidField() {
    if (!this._wizard) return

    const root = this.element
    const allSections = Array.from(root.querySelectorAll('[data-wizard-step]'))
    if (!allSections.length) return

    const stepNums = [...new Set(allSections.map(s => +s.dataset.wizardStep))].sort((a, b) => a - b)

    // Encontra o menor step number que contém algum campo crud-invalid
    let minStepNum = Infinity
    root.querySelectorAll('.crud-invalid').forEach(el => {
      const sec = el.closest('[data-wizard-step]')
      if (sec) {
        const n = +sec.dataset.wizardStep
        if (n < minStepNum) minStepNum = n
      }
    })

    if (minStepNum === Infinity) return // nenhum campo marcado — não redireciona

    const idx = stepNums.indexOf(minStepNum)
    if (idx !== -1) this._wizard.goTo(idx)
  }
}
